'use client'

import {
  Box,
  Text,
  Flex,
  Image,
  Button,
  Icon,
  Avatar,
  Skeleton,
  SkeletonCircle,
} from '@chakra-ui/react'
import { useEffect } from 'react'
import { BottomNav } from '@/components/ui/bottom-nav'
import { FaChevronLeft } from 'react-icons/fa6'
import { GoBell } from 'react-icons/go'
import NextLink from 'next/link'
import { useSubscriptionDetailsQuery } from '@/src/queries/subscriptions/subscription-details'
import { useInView } from 'react-intersection-observer'
import React from 'react'
import Link from 'next/link'
import {
  formatSubscriberCount,
  formatUrl,
} from '@/components/video-page/utils/formatOptions'
import NoSubscriptions from '../none/NoPage'
import GuestView from '../guest/GuestView'
import { useToken } from '@/src/store/token-store'
import { useUnsubscribeChannelMutation } from '@/src/mutations/video/subscribe'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { getInitials } from '@/components/ui/avatar'

interface Subscription {
  channelId: string
  subscriberCount: string
  videoCount: string
  firstName: string
  imageUrl: string
  channelName: string
  channelShareName: string
  isVerifiedTick: string
  themeColor: string
}

const LoadingChannelCard: React.FC = () => {
  return (
    <>
      <Flex
        w={['100%', '100%', '90%']}
        justifyContent="space-between"
        alignItems="center"
        my={2}
        gap={[2, 2, 8]}
      >
        <SkeletonCircle
          w={['40px', '40px', '140px']}
          h={'auto'}
          aspectRatio={1}
          rounded="full"
          color="white"
        />

        <Flex flex={1} direction={'column'} justifyContent={'center'}>
          <Skeleton
            w="60%"
            h={['1rem', '1rem', '1.125rem']}
            maxH={['1rem', 'rem', '1.125rem']}
            m="2"
          />
          <Skeleton
            w="50%"
            h={['0.875rem', '0.875rem', '1rem']}
            maxH={['0.875rem', '0.875rem', '1rem']}
            m="2"
          />
        </Flex>
      </Flex>
    </>
  )
}

const ChannelCard: React.FC<Subscription> = ({
  channelId,
  subscriberCount,
  videoCount,
  firstName,
  imageUrl,
  channelName,
  channelShareName,
  isVerifiedTick,
  themeColor,
}) => {
  const unsubscribeChannelMutation = useUnsubscribeChannelMutation()

  const queryClient = useQueryClient()
  const router = useRouter()

  function handleSubscribe() {
    unsubscribeChannelMutation.mutate(
      {
        channelId,
        eventId: '',
      },
      {
        onSuccess: () => {
          toast.success('Unsubscribed to the channel')
        },
        onError: () => {
          toast.error('Failed to unsubscribe to the channel')
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: ['subscription-details'] })
        },
      }
    )
  }

  return (
    <Flex
      w={['100%', '100%', '90%']}
      justifyContent="space-between"
      alignItems="center"
      my={2}
      gap={[2, 2, 8]}
    >
      <Avatar
        size={['lg', 'lg', '2xl']}
        h={'auto'}
        getInitials={getInitials}
        aspectRatio={1}
        rounded="full"
        name={channelName}
        src={formatUrl(imageUrl || '')}
        bg={themeColor || '#E79051'}
        color="white"
        cursor={'pointer'}
        onClick={() => router.push('/c/' + channelShareName)}
      />

      <Flex
        flex={1}
        direction={'column'}
        justifyContent={'center'}
        onClick={() => router.push('/c/' + channelShareName)}
        cursor={'pointer'}
      >
        <Text fontWeight="600" fontSize={['md', 'md', 'lg']}>
          {channelName}&nbsp;&nbsp;
          <Image
            src="/my-channel/verified-tag.svg"
            alt="verified-tag"
            display={
              isVerifiedTick == '1'
                ? 'inline'
                : 'none'
            }
            h="auto"
            w={[6, 6, 7]}
            mb={[1, 1, 1]}
          />
        </Text>
        <Text
          fontSize={['xs', 'xs', 'sm']}
          color="lyk-green"
          display={['none', 'none', 'block']}
        >
          {formatSubscriberCount(subscriberCount)}
        </Text>
      </Flex>

      <Box>
        <Button
          colorScheme="gray"
          leftIcon={<Icon as={GoBell} />}
          px={4}
          rounded="full"
          fontWeight="400"
          size={['sm', 'sm', 'md']}
          onClick={handleSubscribe}
        >
          Subscribed
        </Button>
      </Box>
    </Flex>
  )
}

function ViewSubscriptions() {
  const { ref, inView } = useInView()
  const result = useSubscriptionDetailsQuery()
  const {
    status,
    data,
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = result
  const hasNextPageRef = React.useRef<boolean>(hasNextPage)

  useEffect(() => {
    hasNextPageRef.current = hasNextPage
  }, [hasNextPage])

  useEffect(() => {
    if (
      inView &&
      hasNextPageRef.current &&
      !isFetchingNextPage &&
      !isFetching
    ) {
      fetchNextPage()
    }
  }, [inView, isFetchingNextPage, fetchNextPage, isFetching])

  let ChannelContent: React.ReactNode = null
  if (status == 'pending') {
    ChannelContent = (
      <>
        {Array(2)
          .fill('-')
          .map((_, i) => (
            <LoadingChannelCard key={i} />
          ))}
      </>
    )
  }

  if (data?.pages.flatMap((page) => page.subscriptions).length === 0) {
    return <NoSubscriptions />
  }

  if (status == 'success') {
    ChannelContent = data?.pages.map((page, i) => (
      <React.Fragment key={i}>
        {page.subscriptions.map((subscription) => (
          <ChannelCard
            key={subscription.channelId}
            channelId={subscription.channelId}
            subscriberCount={subscription.subscriberCount}
            firstName={subscription.firstName}
            imageUrl={subscription.imageUrl}
            channelName={subscription.channelName}
            channelShareName={subscription.channelShareName}
            isVerifiedTick={subscription.isVerifiedTick}
            themeColor={subscription.themeColor}
            videoCount={subscription.videoCount}
          />
        ))}
      </React.Fragment>
    ))
  }

  if (status == 'error') {
    return (
      <Flex
        w="100%"
        flex="1"
        flexDir="column"
        align="center"
        justify="center"
        mt={40}
      >
        <Image src="/failed.svg" width={150} height={150} alt="Failed Icon" />
        <Text mt={4} color="black" fontWeight={600} fontSize={18}>
          Failed to load channels!
        </Text>
        <Button
          _hover={{ opacity: 0.5 }}
          bg={'lyk-green'}
          color={'white'}
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
          href={'/home'}
          as={Link}
          mt={4}
          px={2}
        >
          Go back
        </Button>
      </Flex>
    )
  }

  return (
    <Flex justify={'flex-end'} minH="100vh">
      <Box w="100%" zIndex={2} pb={[20, 10]} display="flex" flexDir="column">
        <Box pb={10} px={[4, 4, 4]} flex="1" w="100%">
          <Flex w="100%" mb={2}>
            <Button
              variant="ghost"
              leftIcon={<Icon as={FaChevronLeft} />}
              fontWeight="400"
              as={NextLink}
              href="/subscriptions"
              px="2"
            >
              Go back
            </Button>
          </Flex>

          <Text
            fontSize="xl"
            fontWeight="600"
            mb={3}
            display={['block', 'block', 'none']}
          >
            Subscribed Channels
          </Text>

          <Flex w="100%" flexDir="column" alignItems="center" gap={[2, 2, 4]}>
            {ChannelContent}
            {isFetching &&
              Array(2)
                .fill('-')
                .map((_, i) => <LoadingChannelCard key={i} />)}
            <div ref={ref} className="pb-10"></div>
          </Flex>
        </Box>
      </Box>
      <BottomNav />
    </Flex>
  )
}

export default function SubsciptionDetails() {
  const { accessToken } = useToken()

  if (!accessToken) {
    return (
      <>
        <GuestView />
      </>
    )
  }

  return (
    <>
      <ViewSubscriptions />
    </>
  )
}
